/**
 * The held screen's frame (Figma 4, 6:2): `HeldBody` is the two columns under the bar (the entries on the left,
 * the page with its box on the right), `HeldListHead` the entries' title, explainer and tally chips,
 * `HeldRow` one entry (its label and reason, its page, the open one on the raised ground), `EntryBar`
 * the right column's header (the entry, what it lacks, its status) and `ActionBar` the footer under the page
 * (what the entry is, then its acts). Pure presentation: words and acts in.
 */
import type { ReactNode } from 'react';
import { Button } from 'react-aria-components';
import { HELD, SPACE, TEXT, WEIGHT, usePalette } from '../theme';
import { focusRing } from './action-button';

export function HeldBody({ list, main }: { list: ReactNode; main: ReactNode }) {
  const p = usePalette();
  return (
    <div style={{ display: 'flex', width: '100%', height: '100%', minHeight: 0 }}>
      <aside
        aria-label="Held entries"
        style={{
          width: HELD.list,
          flexShrink: 0,
          boxSizing: 'border-box',
          borderRight: `1px solid ${p.hairline}`,
          background: p.surface,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {list}
      </aside>
      <section aria-label="The entry" style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', background: p.surfaceRaised }}>
        {main}
      </section>
    </div>
  );
}

export function HeldListHead({ title, note, tally }: { title: ReactNode; note: ReactNode; tally: ReactNode }) {
  const p = usePalette();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.snug, padding: `${SPACE.cozy}px ${SPACE.lg}px`, borderBottom: `1px solid ${p.hairline}` }}>
      <h2 style={{ margin: 0, fontSize: TEXT.body, fontWeight: WEIGHT.strong, color: p.ink, lineHeight: 'normal' }}>{title}</h2>
      <p style={{ margin: 0, fontSize: TEXT.control, color: p.inkSecondary, lineHeight: 'normal' }}>{note}</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACE.tight }}>{tally}</div>
    </div>
  );
}

/** One held entry: what it is and why it is held on the left, the page it sits on at the right. */
export function HeldRow({
  label,
  reason,
  page,
  open,
  onOpen,
}: {
  label: string;
  reason: string;
  /** The page the entry sits on ("p. 44"), or the words that say it names none. */
  page: string;
  /** Whether this is the entry standing open on the right. */
  open: boolean;
  onOpen: () => void;
}) {
  const p = usePalette();
  return (
    <Button
      onPress={onOpen}
      aria-label={`${label}, ${reason}, ${page}`}
      aria-current={open ? 'true' : undefined}
      style={(s) => ({
        display: 'flex',
        gap: SPACE.sm,
        width: '100%',
        padding: `${SPACE.md}px ${SPACE.lg}px`,
        border: 'none',
        borderBottom: `1px solid ${p.hairline}`,
        background: open ? p.surfaceRaised : 'transparent',
        font: 'inherit',
        textAlign: 'left',
        lineHeight: 'normal',
        cursor: 'pointer',
        outline: 'none',
        boxSizing: 'border-box',
        boxShadow: s.isFocusVisible ? focusRing(p) : undefined,
      })}
    >
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: SPACE.xxs }}>
        <span style={{ fontSize: TEXT.control, fontWeight: WEIGHT.medium, color: p.ink }}>{label}</span>
        <span style={{ fontSize: TEXT.caption, color: p.inkSecondary }}>{reason}</span>
      </span>
      <span style={{ fontSize: TEXT.caption, color: p.inkMuted, whiteSpace: 'nowrap' }}>{page}</span>
    </Button>
  );
}

/** One printed line's place on the page: its reading order and the rows it spans, in the page's own pixels. */
export type GutterBand = { readonly number: number; readonly y0: number; readonly y1: number };

/**
 * The printed-line gutter beside the page excerpt (Figma 4:2): each printed line numbered 1..N by its rank in
 * reading order, a small muted numeral at the line's own height. The page's pixel height fixes where a band sits
 * as a fraction of the sheet, so it holds at any width; there are exactly as many numerals as bands.
 */
export function LineGutter({ bands, height }: { bands: readonly GutterBand[]; height: number }) {
  const p = usePalette();
  const ranked = [...bands].sort((a, b) => a.number - b.number);
  return (
    <ol
      aria-label="Printed lines"
      style={{ listStyle: 'none', margin: 0, padding: 0, position: 'absolute', top: 0, bottom: 0, left: SPACE.sm, pointerEvents: 'none' }}
    >
      {ranked.map((band, index) => (
        <li
          key={band.number}
          style={{
            position: 'absolute',
            top: `${(band.y0 / height) * 100}%`,
            height: `${((band.y1 - band.y0) / height) * 100}%`,
            display: 'flex',
            alignItems: 'center',
            fontSize: TEXT.micro,
            color: p.inkFaint,
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 'normal',
          }}
        >
          {index + 1}
        </li>
      ))}
    </ol>
  );
}

/** The page with its gutter laid over its left margin: the one box both are measured in. */
export function GutteredPage({ gutter, children }: { gutter: ReactNode; children: ReactNode }) {
  return (
    <div style={{ position: 'relative' }}>
      {children}
      {gutter}
    </div>
  );
}

export function EntryBar({ label, note, status }: { label: ReactNode; note: ReactNode; status: ReactNode }) {
  const p = usePalette();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.snug, padding: `${SPACE.sm}px ${SPACE.lg}px`, background: p.surface, borderBottom: `1px solid ${p.hairline}` }}>
      <span style={{ fontSize: TEXT.control, fontWeight: WEIGHT.medium, color: p.ink, lineHeight: 'normal', whiteSpace: 'nowrap' }}>{label}</span>
      <span style={{ fontSize: TEXT.body, color: p.inkMuted, lineHeight: 'normal', whiteSpace: 'nowrap' }}>{note}</span>
      <span style={{ flex: 1 }} />
      {status}
    </div>
  );
}

/** The ground the page stands on: centred, scrolling when the sheet is taller than the column. */
export function HeldCanvas({ children }: { children: ReactNode }) {
  return (
    <div
      role="region"
      aria-label="Printed page"
      tabIndex={0}
      style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: `${SPACE.wide}px ${SPACE.xl}px` }}
    >
      <div style={{ width: HELD.sheet, maxWidth: '100%', flexShrink: 0 }}>{children}</div>
    </div>
  );
}

export function ActionBar({ note, children }: { note: ReactNode; children: ReactNode }) {
  const p = usePalette();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm, padding: `${SPACE.md}px ${SPACE.lg}px`, background: p.surface, borderTop: `1px solid ${p.hairline}` }}>
      <span style={{ fontSize: TEXT.control, color: p.inkSecondary, lineHeight: 'normal', whiteSpace: 'nowrap' }}>{note}</span>
      <span style={{ flex: 1 }} />
      {children}
    </div>
  );
}
